import { isDemoMode } from "../config/runtime";
import type { paths } from "../deferred-api/contracts";
import { apiClient } from "./client";
import { CyraError } from "./errors";
import { throwApiError } from "./response-error";

export type AdminLoginCompletion =
  paths["/v1/auth/admin-login/complete"]["post"]["responses"][200]["content"]["application/json"];

export const demoAdminLoginToken = "synthetic-admin-email-code-token";
let demoChallenge:
  | { email: string; requestedAt: number; expiresAt: number; attempts: number }
  | undefined;

export async function requestAdminLogin(email: string): Promise<void> {
  const recipient = email.trim().toLowerCase();
  if (isDemoMode) {
    if (!/^\S+@\S+\.\S+$/u.test(recipient))
      throw new CyraError({
        code: "VALIDATION_FAILED",
        message: "Enter a valid email address.",
        requestId: "synthetic-login-request",
        status: 422,
      });
    if (demoChallenge?.email === recipient && Date.now() - demoChallenge.requestedAt < 60_000)
      throw new CyraError({
        code: "RATE_LIMIT_EXCEEDED",
        message: "Wait before requesting another code.",
        requestId: "synthetic-login-rate-limit",
        status: 429,
        retryAfterSeconds: Math.ceil((demoChallenge.requestedAt + 60_000 - Date.now()) / 1_000),
      });
    demoChallenge = {
      email: recipient,
      requestedAt: Date.now(),
      expiresAt: Date.now() + 600_000,
      attempts: 0,
    };
    return;
  }
  const result = await apiClient.POST("/v1/auth/admin-login/request", {
    body: { email: recipient },
  });
  if (result.data?.status !== "LOGIN_EMAIL_ACCEPTED")
    throwApiError(result.error, result.response, "A sign-in code could not be requested.");
}

export async function completeAdminLogin(
  email: string,
  code: string,
): Promise<AdminLoginCompletion> {
  const recipient = email.trim().toLowerCase();
  const digits = code.trim();
  if (isDemoMode) {
    const challenge = demoChallenge;
    if (
      !challenge ||
      challenge.email !== recipient ||
      challenge.expiresAt <= Date.now() ||
      challenge.attempts >= 5 ||
      digits !== "123456"
    ) {
      if (challenge) challenge.attempts += 1;
      throw new CyraError({
        code: "AUTH_OTP_INVALID_OR_EXPIRED",
        message: "The code is incorrect or expired. Request another code and try again.",
        requestId: "synthetic-login-complete",
        status: 400,
      });
    }
    demoChallenge = undefined;
    return { next: "SIGNED_IN", customToken: demoAdminLoginToken };
  }
  const result = await apiClient.POST("/v1/auth/admin-login/complete", {
    body: { email: recipient, code: digits },
  });
  if (result.data?.next === "SIGNED_IN" && result.data.customToken) return result.data;
  return throwApiError(result.error, result.response, "The sign-in code could not be verified.");
}
